import {Component, EventEmitter, Input, Output, SimpleChanges} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {TranslocoService} from '@ngneat/transloco';
import {take} from "rxjs";
import {AbstractComponent} from "@components";
import {Lookups, OrganiserPaymentData} from "@model";
import {AuthenticationService, OrganiserPaymentDataService, TOAST_STATE, ToastService} from "@services";
import {required} from "@utility";

@Component({
  selector: 'app-organiser-payment-data-form',
  templateUrl: './organiser-payment-data-form.component.html',
  styleUrls: ['./organiser-payment-data-form.component.scss']
})
export class OrganiserPaymentDataFormComponent extends AbstractComponent {

  @Input() paymentData: OrganiserPaymentData | undefined;
  @Input() organiserId: number | undefined;
  @Input() currencies: Lookups[] = [];
  @Output() paymentDataChanged = new EventEmitter<OrganiserPaymentData>();

  public isTaxDataCollapsed: boolean = false;
  public isBanksDataCollapsed: boolean = false;

  inputForm = new FormGroup({
    taxNumber: new FormControl(),
    paysPDV: new FormControl(),
    exemptionText: new FormControl(),
    defaultCurrency: new FormControl(),
    iban: new FormControl(),
    swift: new FormControl(),
    bankName: new FormControl(),
    bankAddress: new FormControl(),
    bankPlace: new FormControl(),
    bankZip: new FormControl(),
  }, {updateOn: 'change'});

  constructor(private paymentService: OrganiserPaymentDataService, private translocoService: TranslocoService,
              private toastService: ToastService, public authenticationService:AuthenticationService) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(_: SimpleChanges): void {
    this.initForm();
  }

  initForm(): void {
    if (this.paymentData) {
      this.inputForm.patchValue(this.paymentData);
      if (!(this.paymentData.paysPDV === false)) {
        this.inputForm.controls.exemptionText.disable();
        this.inputForm.controls.paysPDV.setValue(true);
      }
    }
    this.subscriptions.push(this.inputForm.valueChanges.pipe(take(1)).subscribe(() => {
      this.inputForm.markAsTouched();
    }));
    this.subscriptions.push(this.inputForm.controls.paysPDV.valueChanges.subscribe((value: boolean) => {
      this.inputForm.controls.exemptionText.clearValidators();
      if (!value) {
        this.inputForm.controls.exemptionText.enable();
        this.inputForm.controls.exemptionText.addValidators([required]);
      } else {
        this.inputForm.controls.exemptionText.setValue(undefined);
        this.inputForm.controls.exemptionText.disable();
      }
      this.inputForm.controls.exemptionText.updateValueAndValidity();
      this.inputForm.markAsTouched();
    }));
    this.inputForm.controls.taxNumber.addValidators([required]);
    this.inputForm.controls.paysPDV.addValidators([required]);
  }

  private paymentDataFields(): OrganiserPaymentData {
    this.checkValidators(this.inputForm);
    let data: OrganiserPaymentData = {...this.paymentData}
    data.organiserId = this.organiserId;
    data.taxNumber = this.inputForm.value.taxNumber;
    data.paysPDV = this.inputForm.value.paysPDV;
    data.exemptionText = this.inputForm.value.exemptionText;
    data.defaultCurrency = this.inputForm.value.defaultCurrency;
    data.iban = this.inputForm.value.iban;
    data.swift = this.inputForm.value.swift;
    data.bankName = this.inputForm.value.bankName;
    data.bankPlace = this.inputForm.value.bankPlace;
    data.bankZip = this.inputForm.value.bankZip;
    return data;
  }

  onSave() {
    if (!this.inputForm.valid || !this.organiserId || !this.inputForm.touched) {
      return;
    } else {
      let organiserPaymentData: OrganiserPaymentData = this.paymentDataFields();
      if (organiserPaymentData !== undefined) {
        this.paymentService.updatePaymentData(organiserPaymentData, () => {
          this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("app.common.success.payment.data.edit"))
          this.paymentDataChanged.emit(organiserPaymentData);
        }, () => {
          this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.common.server.error"))
        });
      }
    }
  }

  onCancel() {
    this.inputForm.reset();
  }
}
